import * as React from 'react'
import { RansackQuery } from '../../../services/models/offered-service-resources.service'
import { OfferedServiceType } from '../../../services/models/offered-services.service'
import { I18nService } from '../../../services/utils/i18n.service'
import AsyncButtonComponent from '../../shared/async-button.component'
import { Select } from '../../shared/select.component'
import { OfferedServiceBase } from '../../../services/json-schemas/base/offered_service'
import { ServiceDeliveryLocationBase } from '../../../services/json-schemas/base/service_delivery_location'

interface Props {
  query?: RansackQuery
  offeredServices: { id: OfferedServiceBase['id'], name: string, offered_service_type: OfferedServiceType }[]
  availableForBody?: RansackQuery['available_for']
  serviceDeliveryLocations?: { id: ServiceDeliveryLocationBase['id'], name: string, inline_address: string }[]
  onChange: (query: RansackQuery) => void
  onSubmit: () => Promise<any>
}

const i18nScoped = new I18nService(require('./offered-service-resources-filter.component.yml'))

export default function OfferedServiceResourcesFilterComponent ({ query, offeredServices, availableForBody, serviceDeliveryLocations, onChange, onSubmit }: Props) {
  const setQueryState = React.useCallback((body: RansackQuery) => {
    onChange({ ...query, ...body })
  }, [onChange, query])

  const serviceDeliveryLocationChoices = React.useMemo(() => {
    return [{ value: undefined as ServiceDeliveryLocationBase['id'], label: i18nScoped.t('all_service_delivery_locations') }]
    .concat(serviceDeliveryLocations.map(_ => ({ value: _.id, label: `${_.name} (${_.inline_address})` })))
  }, [serviceDeliveryLocations])

  const serviceChoices = React.useMemo(() => {
    return [{ value: undefined as OfferedServiceBase['id'], label: i18nScoped.t('all_products') }].concat(
      (offeredServices || [])
        .filter(_ => _.offered_service_type === 'product')
        .map(_ => ({ value: _.id, label: _.name }))
        .sort((a, b) => a.label.localeCompare(b.label))
    )
  }, [offeredServices])

  return <>
    { !!offeredServices?.length && <>
      <label className="form-label">{ i18nScoped.t('offered_product') }</label>
      <Select
        options={ serviceChoices }
        value={ serviceChoices.find(_ => _.value === query['q[find_by_offered_service]']) }
        onChange={ choice => setQueryState({ 'q[find_by_offered_service]': choice.value }) } />
    </> }

    <label className="form-label u-mts">{ i18nScoped.t('intervention_sector') }</label>
    <input
      type="text"
      placeholder={ i18nScoped.t('intervention_sector_placeholder') }
      value={ query['q[intervention_sector]'] ?? '' }
      onChange={ evt => setQueryState({ 'q[intervention_sector]': evt.target.value || undefined }) } />

    { serviceDeliveryLocationChoices.length > 1 && <>
      <label className="form-label u-mts">{ i18nScoped.t('service_delivery_location') }</label>

      <Select
        options={ serviceDeliveryLocationChoices }
        value={ serviceDeliveryLocationChoices.find(_ => _.value === query['q[find_by_service_delivery_location]']) }
        onChange={ choice => setQueryState({ 'q[find_by_service_delivery_location]': choice.value }) } />
    </> }

    { availableForBody && <div className="inputCheckbox u-mtm">
      <input id="available" type="checkbox" checked={ !!query.available_for } onChange={ event => setQueryState({ available_for: event.target.checked ? availableForBody : undefined }) }/>
      <label htmlFor="available">{ i18nScoped.t('available') }</label>
    </div> }

    <AsyncButtonComponent className="button button--S button--block u-mtm" onClick={ onSubmit } onWait="prefix">{ i18nScoped.t('search') }</AsyncButtonComponent>
  </>
}
